import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";
import { fetchIdibuIntegration, requestZapierIntegration } from "../../../Redux/ActionCreators/idibuAction";
import { ContactGappeoModal, IdibuDialog, STATUS_LABEL } from "./IdibuConnect";
import FeatureNotAvailable, { isFreePlan } from "./FeatureNotAvailable";
import "./integrations.scss";

const IntegrationCard = ({ logo, logoClass, title, description, badge, badgeClass, action, onClick }) => (
  <button type="button" className="integration-card" onClick={onClick}>
    <div className="integration-card-head">
      <span className={`integration-logo ${logoClass}`}>{logo}</span>
      {badge ? <span className={`integration-badge ${badgeClass || ""}`}>{badge}</span> : null}
    </div>
    <h5>{title}</h5>
    <p>{description}</p>
    <span className="integration-card-action">{action} →</span>
  </button>
);

/** Settings > Integration: Gappeo API keys, idibu and Zapier. */
const Integrations = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedTenant } = useSelector((state) => state.users);
  const [idibu, setIdibu] = useState(null);
  const [showZapier, setShowZapier] = useState(false);
  const [upgradeFor, setUpgradeFor] = useState(null);
  const [snack, setSnack] = useState(null);

  const apiIntegrationEnabled =
    selectedTenant?.data?.subscription_plan?.configuration?.feature_flags?.api_integration === true;
  const freePlan = isFreePlan(selectedTenant);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const result = await dispatch(fetchIdibuIntegration());
      if (mounted) setIdibu(result?.success ? result.data : {});
    })();
    return () => {
      mounted = false;
    };
  }, [dispatch]);

  const idibuStatus = idibu?.status || "not_connected";
  const showIdibu = idibu !== null && idibu.platform_enabled !== false;

  return (
    <div className="job-table-container settings-fill-page">
      <div className="top-navigation">
        <div className="createjob-tab">
          <h4>Integration</h4>
        </div>
      </div>
      <div className="table-content" style={{ background: "#FFFFFF" }}>
        <div className="job-settings integrations-scroll">
          <p className="integrations-intro">
            Connect Gappeo with your own systems and the tools your team already uses.
          </p>
          <div className="integration-grid">
            <IntegrationCard
              logo="G"
              logoClass="gappeo"
              title="Gappeo API Keys"
              description="Create API keys so your own systems can call the Gappeo API."
              badge={apiIntegrationEnabled ? "Available" : "Upgrade plan"}
              badgeClass={apiIntegrationEnabled ? "active" : ""}
              action="Manage"
              onClick={() => (apiIntegrationEnabled ? navigate("/keys") : setUpgradeFor("Gappeo API Keys"))}
            />
            {showIdibu ? (
              <IntegrationCard
                logo="idibu"
                logoClass="idibu"
                title="idibu Integration"
                description="Post jobs to job boards and receive applicants through your idibu account."
                badge={freePlan ? "Upgrade plan" : idibu.status ? STATUS_LABEL[idibuStatus] || idibuStatus : null}
                badgeClass={freePlan ? "" : idibuStatus}
                action={freePlan || idibuStatus === "active" ? "Manage" : "Connect"}
                onClick={() => (freePlan ? setUpgradeFor("idibu Integration") : navigate("/integrations/idibu"))}
              />
            ) : null}
            <IntegrationCard
              logo="Z"
              logoClass="zapier"
              title="Zapier"
              description="Automate workflows between Gappeo and the other apps your team uses."
              badge={freePlan ? "Upgrade plan" : "Contact us"}
              action={freePlan ? "Manage" : "Contact Gappeo"}
              onClick={() => (freePlan ? setUpgradeFor("Zapier") : setShowZapier(true))}
            />
          </div>
        </div>
      </div>
      {upgradeFor ? (
        <IdibuDialog title={upgradeFor} onClose={() => setUpgradeFor(null)}>
          <FeatureNotAvailable />
        </IdibuDialog>
      ) : null}
      {showZapier ? (
        <ContactGappeoModal
          title="Contact Gappeo for Zapier"
          intro="Tell us what you'd like to automate and our team will help you set up the Zapier integration."
          messagePlaceholder="Please enter your message"
          submitAction={requestZapierIntegration}
          onClose={() => setShowZapier(false)}
          onSent={() => setSnack("Thanks! The Gappeo team will contact you about Zapier.")}
        />
      ) : null}
      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setSnack(null)}>
          {snack}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Integrations;
